// User metadata key marking that the profile onboarding form (name, job type,
// favorite platforms) hasn't been completed yet. Set (to "true") at the user's
// first login and cleared when they submit the profile form on the /welcome
// page.
import { z } from "zod";

export const ONBOARDING_PROFILE_PENDING_METADATA_KEY =
  "onboarding:profile_pending";

export const TIMEZONE_METADATA_KEY = "timezone";

export const ONBOARDING_STEP_METADATA_KEY = "onboarding:step";

// Written at first login (including invitation) and by POST
// /api/user/onboarding/complete. The workspace layout redirects only on this
// value, then the wizard consumes it so later visits are not forced back in —
// in-progress step ids are resume state, not a gate.
export const ONBOARDING_STEP_QUEUED_VALUE = "pending";
export const ONBOARDING_STEP_DONE_VALUE = "done";

export const COS_TONE_METADATA_KEY = "cos_tone";

export const cosAgentNameMetadataKey = "cos_agent_name";

export const cosPreferredNameMetadataKey = "cos_preferred_name";

export const ONBOARDING_CONVERSATION_METADATA_KEY = "onboarding:conversation";

// Written once the signup seed has run for this user in this workspace. The
// marker, rather than "has no routines", is what makes the seed a one-off: a
// user who pauses or deletes Morning Briefing meant it, and a later login must
// not put it back.
export const ONBOARDING_ROUTINES_SEEDED_METADATA_KEY = "onboarding:routines";

// Written on the person, not a workspace, once the founder's welcome mail has
// gone out: one welcome per person, however many workspaces they finish.
export const ONBOARDING_WELCOME_EMAIL_METADATA_KEY = "onboarding:welcome_email";

export const ONBOARDING_GMAIL_ENRICHMENT_METADATA_KEY =
  "onboarding:gmail_enrichment";

export const ONBOARDING_GMAIL_ENRICHMENT_STATUSES = [
  "pending",
  "running",
  "complete",
  "failed",
] as const;

export type OnboardingGmailEnrichmentStatus =
  (typeof ONBOARDING_GMAIL_ENRICHMENT_STATUSES)[number];

// Kept apart from the enrichment state: the backfill writes it from Temporal
// while the chat loop writes the state, and one key per writer means neither
// can overwrite the other's update.
export const ONBOARDING_WIKI_BUILD_STAGE_METADATA_KEY =
  "onboarding:wiki_build_stage";

// In build order, then the two terminal stages. Onboarding lists the phases
// in this order; "failed" is written only once Temporal gives up on the build.
export const WIKI_BUILD_STAGES = [
  "finding_mail",
  "reading_mail",
  "extracting_facts",
  "writing_profile",
  "finding_people",
  "understanding_people",
  "organizing",
  "writing_pages",
  "finding_tasks",
  "done",
  "failed",
] as const;

export type WikiBuildStage = (typeof WIKI_BUILD_STAGES)[number];

export function isWikiBuildStage(value: unknown): value is WikiBuildStage {
  return WIKI_BUILD_STAGES.some((stage) => stage === value);
}

// What onboarding waits for: the owner's profile. Every later stage runs in
// the background while the owner carries on.
export const WIKI_PROFILE_BUILD_STAGES = [
  "finding_mail",
  "reading_mail",
  "extracting_facts",
  "writing_profile",
] as const satisfies readonly WikiBuildStage[];

export type WikiProfileBuildStage = (typeof WIKI_PROFILE_BUILD_STAGES)[number];

export function isWikiProfileBuildStage(
  stage: WikiBuildStage
): stage is WikiProfileBuildStage {
  return WIKI_PROFILE_BUILD_STAGES.some(
    (profileStage) => profileStage === stage
  );
}

const OnboardingGmailEnrichmentStateSchema = z.object({
  status: z.enum(ONBOARDING_GMAIL_ENRICHMENT_STATUSES),
  updatedAt: z.string().datetime(),
  agentMessageId: z.string().nullable(),
  // Fixed at kickoff and carried through later transitions, unlike `updatedAt`,
  // so it can bound which suggestions this pass produced. Null for state
  // written before the field existed, which attributes nothing.
  startedAt: z.string().datetime().nullable().default(null),
});

export type OnboardingGmailEnrichmentState = z.infer<
  typeof OnboardingGmailEnrichmentStateSchema
>;

export function parseOnboardingGmailEnrichmentState(
  value: string | null
): OnboardingGmailEnrichmentState | null {
  if (!value) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(value);
    const result = OnboardingGmailEnrichmentStateSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function serializeOnboardingGmailEnrichmentState(
  state: OnboardingGmailEnrichmentState
): string {
  return JSON.stringify(OnboardingGmailEnrichmentStateSchema.parse(state));
}

export const inviteBannerDismissedMetadataKey = "invite_banner_dismissed";
